/**
 * A measured estimate of the time left while homework pages are read (#619).
 *
 * Pages are read concurrently (#571), so the pace is throughput: the time from the upload being accepted to the
 * latest page finishing, divided by the pages finished. It is a measurement of what has happened, never a promise,
 * and it needs at least two finished pages and sane timestamps; otherwise it says nothing.
 */

export type ReadingEstimate = { readonly kind: 'minutes'; readonly minutes: number } | { readonly kind: 'under_a_minute' };

const MINUTE_MS = 60_000;

export function estimateTimeLeft(input: {
  readonly createdAt: string | undefined;
  /** One entry per page the server lists: the instant its reading finished, or null/undefined while it is being read. */
  readonly readAts: readonly (string | null | undefined)[];
  /** Pages in the PDF. */
  readonly total: number;
}): ReadingEstimate | null {
  if (input.createdAt === undefined) return null;
  const start = Date.parse(input.createdAt);
  if (!Number.isFinite(start)) return null;
  const finished: number[] = [];
  for (const readAt of input.readAts) {
    if (readAt === null || readAt === undefined) continue;
    const at = Date.parse(readAt);
    if (!Number.isFinite(at) || at < start) return null; // bad data: say nothing rather than guess
    finished.push(at);
  }
  const remaining = input.total - finished.length;
  if (finished.length < 2 || remaining <= 0) return null;
  const elapsed = Math.max(...finished) - start;
  if (elapsed <= 0) return null;
  const leftMs = (elapsed / finished.length) * remaining;
  if (leftMs < MINUTE_MS) return { kind: 'under_a_minute' };
  return { kind: 'minutes', minutes: Math.ceil(leftMs / MINUTE_MS) };
}
